import React, { useState } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import Icons from '../../../../../components/Icons/Icons';
import InfoPopup from '../../../../../components/InfoPopup/InfoPopup';
import GuidePopup from '../../../../../components/GuidePopup/GuidePopup';
import { useIntl } from 'react-intl';

import '../../../../../styles/react-tooltip.css';
import css from './SignatureServices.module.css';
import { TALENT_SERVICES_GUIDES } from '../../../../../util/enums';

const appearanceOptions = [
  { title: 'Autograph Session', guide: TALENT_SERVICES_GUIDES.AUTOGRAPH_SESSION },
  { title: 'Camp Participation', guide: TALENT_SERVICES_GUIDES.CAMP_PARTICIPATION },
  { title: 'Digital Interview', guide: TALENT_SERVICES_GUIDES.DIGITAL_INTERVIEW },
  { title: 'Esports Event Appearance', guide: TALENT_SERVICES_GUIDES.E_SPORTS_APPEARANCE },
  { title: 'Fundraising Event Appearance', guide: TALENT_SERVICES_GUIDES.FUNDRAISING_EVENT },
  { title: 'Game Appearance', guide: TALENT_SERVICES_GUIDES.GAME_APPEARANCE },
  { title: 'Golf Outing', guide: TALENT_SERVICES_GUIDES.GOLF_OUTING },
  { title: 'Halftime Appearance', guide: TALENT_SERVICES_GUIDES.HALFTIME_APPEARANCE },
  { title: 'In-person Interview', guide: TALENT_SERVICES_GUIDES.IN_PERSON_INTERVIEW },
  { title: 'Media Day Appearance', guide: TALENT_SERVICES_GUIDES.MEDIA_DAY_APPEARANCE },
  { title: 'Meet and Greet', guide: TALENT_SERVICES_GUIDES.MEET_AND_GREET },
  { title: 'Podcast', guide: TALENT_SERVICES_GUIDES.PODCAST_APPEARANCE },
  { title: 'Practice Appearance', guide: TALENT_SERVICES_GUIDES.PRACTICE_APPEARANCE },
  { title: 'Production Shoot', guide: TALENT_SERVICES_GUIDES.PRODUCTION_SHOOT },
  { title: 'Skill Demonstration', guide: TALENT_SERVICES_GUIDES.SKILL_DEMONSTRATION },
  { title: 'Speech', guide: TALENT_SERVICES_GUIDES.SPEECH },
  { title: 'Virtual Classroom Visit', guide: TALENT_SERVICES_GUIDES.VIRTUAL_CLASSROOM_VISIT },
  { title: 'Watch Party', guide: TALENT_SERVICES_GUIDES.WATCH_PARTY },
].sort((a, b) => a.title.localeCompare(b.title));

const appearanceTooltips = {
  'Meet and Greet': 'Connect with fans for personal and memorable interactions.',
  'Skill Demonstration': 'Showcase your expertise in exclusive sessions.',
  'Digital Interview': 'Participate in virtual interviews for media or content needs.',
  'In-person Interview': 'Provide insightful, in-person interview experiences.',
  Speech: 'Deliver inspiring speeches at events or gatherings.',
  Podcast: 'Appear as a guest on podcasts for engaging conversation and insights.',
  'Production Shoot': 'Participate in professional photo or video shoots for projects.',
  'Autograph Session': 'Host live autograph signing events for fans and collectors.',
  'Golf Outing': 'Offer rounds of golf for a unique and exclusive experience.',
  'Watch Party': 'Attend watch parties for games or events for an unforgettable experience.',
  'Game Appearance': 'Attend games to engage with fans and create excitement.',
  'Practice Appearance':
    'Visit practice sessions to provide motivation, mentorship, or inspiration.',
  'Camp Participation': 'Join camps to engage with attendees and share expertise.',
  'Virtual Classroom Visit': 'Inspire students by joining classrooms virtually.',
  'Fundraising Event Appearance':
    'Boost engagement at charity or fundraising events with your appearance.',
  'Halftime Appearance': 'Perform during halftime shows for exciting fan experiences.',
  'Media Day Appearance': 'Participate in media days for interviews and promotions.',
  'Esports Event Appearance': 'Attend esports competitions to engage with fans and players.',
};

const Appearances = ({ values }) => {
  const intl = useIntl();
  const [activeIndex, setActiveIndex] = useState(null);

  const handleAccordionClick = index => {
    setActiveIndex(index === activeIndex ? null : index);
  };

  const faqsData = [
    {
      question: intl.formatMessage({
        id: 'Appearances.title',
        defaultMessage: 'Appearances',
      }),
      answer: (
        <div className={css.appearancesFormRow}>
          {appearanceOptions.map((option, index) => (
            <React.Fragment key={index}>
              <div className={css.formFld}>
                <label className={css.talentOptions}>
                  <Field
                    name={`appearances[${index}].selected`}
                    component="input"
                    type="checkbox"
                    format={Boolean}
                    parse={Boolean}
                  />
                  <span>
                    {option.title}
                    {appearanceTooltips[option.title] && (
                      <InfoPopup triggerOn="hover" info={appearanceTooltips[option.title]} />
                    )}
                    {option.guide && <GuidePopup externalLink={option.guide} />}
                  </span>
                </label>

                <Field name={`appearances[${index}].selected`} subscription={{ value: true }}>
                  {({ input }) =>
                    input.value && (
                      <div className={css.customFldSec}>
                        <span className={css.currency}>$</span>
                        <Field
                          name={`appearances[${index}].price`}
                          component="input"
                          className={css.input}
                          type="number"
                          placeholder={`Hourly Price for ${option.title}`}
                        />
                        <span className={css.price}>.00</span>
                      </div>
                    )
                  }
                </Field>
              </div>

              <Field
                name={`appearances[${index}].title`}
                component="input"
                type="hidden"
                initialValue={option.title}
              />
            </React.Fragment>
          ))}
        </div>
      ),
    },
  ];

  return (
    <>
      {faqsData.map((faq, index) => (
        <div
          key={index}
          className={classNames(css.faqCard, {
            [css.activeFAQ]: activeIndex === index,
          })}
          onClick={() => handleAccordionClick(index)}
        >
          <div className={css.faqBody}>
            <span className={css.faqHeading}>
              {faq.question}
              <span className={css.faqIcon}>
                {activeIndex === index ? <Icons name="upAngle" /> : <Icons name="downAngle" />}
              </span>
            </span>
            {activeIndex === index && (
              <div className={css.faqDescription} onClick={e => e.stopPropagation()}>
                {faq.answer}
              </div>
            )}
          </div>
        </div>
      ))}
    </>
  );
};

export default Appearances;
